import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  Avatar,
  Card,
  CardContent,
  IconButton,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputLabel,
  ButtonGroup,
  Tabs,
  Tab,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Menu,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Visibility as ViewIcon,
  Description as InvoiceIcon,
  FilterList as FilterIcon,
  VerifiedUser as VerifiedIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  TableChart as TableChartIcon,
  ViewModule as ViewModuleIcon,
  FileDownload as ExportIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { productService } from '../utils/apiService';
import ConfirmationModal from '../components/print/ConfirmationModal';
import ExportButton from '../components/ExportButton';

const Items = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const navigate = useNavigate();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [filters, setFilters] = useState({
    status: {
      active: true,
      inactive: true,
    },
    group: {
      electronics: true,
      services: true,
      furniture: true,
      other: true
    },
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, [page, rowsPerPage]);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productService.getProducts(page + 1, rowsPerPage);
      console.log('API Response:', response);
      
      if (response && Array.isArray(response)) {
        setItems(response);
        setTotalCount(response.length);
      } else if (response && response.data) {
        setItems(response.data);
        setTotalCount(response.total || response.data.length);
      } else {
        setItems([]);
        setTotalCount(0);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching products:', err);
      setError(err.message || 'Failed to fetch products');
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterChange = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        ...value,
      },
    }));
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      if (itemToDelete) {
        await productService.deleteProduct(itemToDelete._id);
        fetchProducts();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    } finally {
      setDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setItemToDelete(null);
  };

  const handleEditClick = (item) => {
    navigate(`/items/edit/${item._id}`);
  };

  // Simplified filtering logic with tab functionality
  const filteredItems = items.filter(item => {
    if (!item) return false;

    // Search filter
    const searchMatch = searchQuery === '' || 
      (item.productCode && item.productCode.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (item.productName && item.productName.toLowerCase().includes(searchQuery.toLowerCase()));

    // Status filter
    const statusMatch = 
      (filters.status.active && item.isActive === true) ||
      (filters.status.inactive && item.isActive === false);

    // Item type filter based on active tab
    const itemTypeMatch = 
      activeTab === 0 || // All Items tab
      (activeTab === 1 && item.itemType === 'goods') || // Goods tab
      (activeTab === 2 && item.itemType === 'service'); // Services tab
    
    return searchMatch && statusMatch && itemTypeMatch;
  });

  console.log('Filtered items:', filteredItems); // Debug log

  const paginatedItems = filteredItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const exportColumns = [
    { field: 'productCode', headerName: 'Code' },
    { field: 'productName', headerName: 'Name' },
    { field: 'basicUnit', headerName: 'Base Unit' },
    { field: 'group', headerName: 'Group' },
    { field: 'salesRate', headerName: 'Sales Price' },
    { field: 'barcode', headerName: 'Barcode' },
    { field: 'taxRate', headerName: 'VAT' },
    { field: 'hsnCode', headerName: 'HSN Code' },
    { field: 'isActive', headerName: 'Status' }
  ];

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Items
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            View all your items and services
        </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <ExportButton 
            data={filteredItems} 
            columns={exportColumns} 
            filename="items"
          />
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
            size="small"
          onClick={() => navigate('/items/new')}
            sx={{ 
              borderRadius: '4px',
              textTransform: 'none',
              py: 0.5,
              px: 1,
            }}
          >
            Add Item
        </Button>
        </Box>
      </Box>
      
      <Tabs 
        value={activeTab} 
        onChange={handleTabChange}
        sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          mb: 0.5,
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 500,
            minWidth: 120,
            py: 0.5,
            minHeight: 36,
          }
        }}
      >
        <Tab label="All Items" />
        <Tab label="Goods" />
        <Tab label="Services" />
        </Tabs>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
            <TextField
              variant="outlined"
              size="small"
          placeholder="Search for items..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ 
            width: '100%',
            maxWidth: 500,
            height: 28,
                '& .MuiOutlinedInput-root': {
              borderRadius: '2px',
              height: 28,
            },
            '& .MuiInputBase-input': {
              py: 0.5,
                }
              }}
            />
            <Button 
          variant="outlined"
              startIcon={<FilterIcon />} 
              size="small"
              onClick={handleFilterClick}
              sx={{ 
            borderRadius: '2px',
                textTransform: 'none',
            py: 0.25,
            px: 1,
            height: 28,
            ml: 1,
              }}
            >
          Filters
            </Button>
          <Menu
            anchorEl={filterAnchorEl}
            open={Boolean(filterAnchorEl)}
            onClose={handleFilterClose}
            PaperProps={{
              sx: {
              width: 200,
              p: 1,
            },
          }}
        >
          <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>Status</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.active}
                  onChange={(e) => handleFilterChange('status', { active: e.target.checked })}
                  size="small"
                />
              }
              label="Active"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.status.inactive}
                  onChange={(e) => handleFilterChange('status', { inactive: e.target.checked })}
                  size="small"
                />
              }
              label="Inactive"
            />
          </FormGroup>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" sx={{ px: 1, py: 0.5 }}>Group</Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.group.electronics}
                  onChange={(e) => handleFilterChange('group', { electronics: e.target.checked })}
                size="small"
                />
              }
              label="Electronics"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.group.services}
                  onChange={(e) => handleFilterChange('group', { services: e.target.checked })}
                size="small"
                />
              }
              label="Services"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.group.furniture}
                  onChange={(e) => handleFilterChange('group', { furniture: e.target.checked })}
                size="small"
                />
              }
              label="Furniture"
            />
          </FormGroup>
          </Menu>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper 
        elevation={0} 
        sx={{ 
          p: 0.5, 
          mb: 0.5, 
          border: '1px solid rgba(0, 0, 0, 0.12)', 
          borderRadius: '4px',
        }}
      >
        <Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '100px' }}>Code</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '200px' }}>Name</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Base Unit</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Group</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Sales Price</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>Barcode</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>VAT</TableCell>
                  <TableCell align="left" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '120px' }}>HSN Code</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, minWidth: '100px' }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'medium', color: 'text.secondary', py: 0.5, width: '100px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 3 }}>
                      No products found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => (
                    <TableRow 
                      key={item._id} 
                      hover
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                    >
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.productCode || '-'}</TableCell>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 'medium', py: 0.5 }}>
                        {item.productName || '-'}
                      </TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.basicUnit || '-'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.group || '-'}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>{item.salesRate?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.barcode || '-'}</TableCell>
                      <TableCell align="right" sx={{ py: 0.5 }}>{item.taxRate || '0'}%</TableCell>
                      <TableCell align="left" sx={{ py: 0.5 }}>{item.hsnCode || '-'}</TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Chip 
                          label={item.isActive ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={item.isActive ? 'success' : 'error'}
                          variant="outlined"
                          sx={{ 
                            fontWeight: 'medium',
                            backgroundColor: !item.isActive ? 'rgba(211, 47, 47, 0.1)' : 'transparent'
                          }}
                        />
                      </TableCell>
                      <TableCell align="center" sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <IconButton 
                            size="small" 
                            color="primary" 
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => handleEditClick(item)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                            color="error" 
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={() => handleDeleteClick(item)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        </Box>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={filteredItems.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid rgba(0, 0, 0, 0.12)',
              '& .MuiTablePagination-select': {
                py: 0.5,
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: '0.875rem',
              }
            }}
          />
        </Box>
      </Paper>

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Item"
        message={`Are you sure you want to delete "${itemToDelete?.productName}"?`}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </Container>
  );
}

export default Items; 